<script setup lang="ts">
import { useGtm } from '@gtm-support/vue-gtm'
import * as Sentry from '@sentry/vue'
import posthog from 'posthog-js'
import { IS_POSTHOG_ENABLED } from '@/constants/analytics'
import { localize, setLocale } from '@vee-validate/i18n'
import { configure, defineRule } from 'vee-validate'
import {
  alpha,
  alpha_dash,
  alpha_num,
  alpha_spaces,
  between,
  confirmed,
  digits,
  dimensions,
  email,
  ext,
  image,
  integer,
  is,
  is_not,
  length,
  max,
  max_value,
  mimes,
  min,
  min_value,
  not_one_of,
  numeric,
  one_of,
  regex,
  required,
  size
} from '@vee-validate/rules'

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import localeData from 'dayjs/plugin/localeData'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import duration from 'dayjs/plugin/duration'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import 'dayjs/locale/fr'

import useLocalStorage from './composables/useLocalStorage'
import useAxios from './composables/axios'
import useLang from './composables/lang'
import usePlugin from './composables/plugin'
import { IconoirProvider } from '@iconoir/vue'
const { user, isSupportConnection, jwt } = useLocalStorage()
const { setupAxios } = useAxios()
const gtm = useGtm()
const route = useRoute()
const { locale } = useI18n()
const { defaultLang } = useLang()
const { installPlugins } = usePlugin()
const { selectedStore } = useStore()

const mainRef = ref<HTMLDivElement | null>(null)
provide('mainRef', mainRef)

const initDayjsConfig = () => {
  dayjs.extend(localeData)
  dayjs.extend(localizedFormat)
  dayjs.extend(duration)
  dayjs.extend(utc)
  dayjs.extend(isSameOrBefore)
  dayjs.extend(isSameOrAfter)
  dayjs.locale('fr')
}

const setSentryUser = () => {
  if (user.value?.id) {
    Sentry.setUser({
      email: user.value.email || null,
      id: user.value.id.toString(),
      isSupportConnection: isSupportConnection.value
    })
  } else {
    Sentry.setUser(null)
  }
}

const initI18n = () => {
  // Enregistrement des règles individuellement
  defineRule('alpha', alpha)
  defineRule('alpha_dash', alpha_dash)
  defineRule('alpha_num', alpha_num)
  defineRule('alpha_spaces', alpha_spaces)
  defineRule('between', between)
  defineRule('confirmed', confirmed)
  defineRule('digits', digits)
  defineRule('dimensions', dimensions)
  defineRule('email', email)
  defineRule('ext', ext)
  defineRule('image', image)
  defineRule('integer', integer)
  defineRule('is', is)
  defineRule('is_not', is_not)
  defineRule('length', length)
  defineRule('max', max)
  defineRule('max_value', max_value)
  defineRule('mimes', mimes)
  defineRule('min', min)
  defineRule('min_value', min_value)
  defineRule('not_one_of', not_one_of)
  defineRule('numeric', numeric)
  defineRule('one_of', one_of)
  defineRule('regex', regex)
  defineRule('required', required)
  defineRule('size', size)

  configure({ generateMessage: localize(defaultLang.value) })
  setLocale(defaultLang.value)
  locale.value = defaultLang.value
}

// On n'enregistre QUE l'usage des vrais clients : en mode assistance
// (collaborateur interne connecté au compte d'un commerçant), on coupe
// toute capture PostHog, comme on le fait pour GTM.
const setPostHogCapture = () => {
  if (!IS_POSTHOG_ENABLED) {
    return
  }
  if (isSupportConnection.value && !posthog.has_opted_out_capturing()) {
    posthog.opt_out_capturing()
  } else if (!isSupportConnection.value && posthog.has_opted_out_capturing()) {
    posthog.opt_in_capturing({ captureEventName: false })
  }
}

const setup = () => {
  gtm.enable(!isSupportConnection.value)
  setPostHogCapture()
  setSentryUser()
  initI18n()
  setupAxios()
  initDayjsConfig()
}

installPlugins()
setup()
watch([isSupportConnection, () => route.meta.guest, user], () => setup())
watch(jwt, () => setupAxios())

onMounted(() => {
  if ((!selectedStore.value || selectedStore.value < 0) && selectedStore.value !== 0) {
    selectedStore.value = 0
  }
})
</script>

<template>
  <IconoirProvider>
    <TooltipProvider>
      <router-view :key="route.name as string" />
      <AppCopilotWidget />
      <Sonner />
    </TooltipProvider>
  </IconoirProvider>
</template>

<style>
@import 'https://rsms.me/inter/inter.css';
@import '@vueform/slider/themes/default.css';

.slider-connect,
.slider-tooltip {
  background: #5b21b6 !important;
}

.slider-tooltip {
  border: 1px solid #5b21b6;
}
</style>
